import { action, extendObservable } from 'mobx';
import { RootStore } from '../RootStore';
import Web3 from 'web3';

import { IBBTC, IBBTC_ABI, ONE_MIN_MS, WIBBTC, WIBBTC_ABI } from 'config/constants';

import BigNumber from 'bignumber.js';

interface UserBalanceCache {
	[key: string]: CachedTokenBalances;
}

interface TokenBalances {
	[contract: string]: TokenBalance;
}

export interface TokenBalance {
	balance: BigNumber;
	displayBalance: string;
}

interface CachedTokenBalances {
	key: string;
	tokens: TokenBalances;
	expiry: number;
}

export default class UserStore {
	private store: RootStore;
	private userBalanceCache: UserBalanceCache = {};

	// loading: undefined, error: null, present: object
	public tokenBalances: TokenBalances = {};
	public loadingBalances: boolean;
	public pricePerShare: BigNumber;

	constructor(store: RootStore) {
		this.store = store;
		this.loadingBalances = false;
		this.pricePerShare = new BigNumber(1 * 1e18);

		extendObservable(this, {
			tokenBalances: this.tokenBalances,
			loadingBalances: this.loadingBalances,
			pricePerShare: this.pricePerShare
		});
	}

	/* Read Variables */
	async reloadBalances(address?: string): Promise<void> {
		const { onboard } = this.store;
		const actions = [];
		const queryAddress = address ?? onboard.address;

		if (queryAddress) {
			actions.push(this.updateBalances());
			await Promise.all(actions);
		}
	}

	async reloadWrapperInfo(): Promise<void> {
		const { onboard } = this.store;
		if (onboard.address) {
			await this.updateWrapperInfo();
		}
	}

	updateWrapperInfo = action(async (): Promise<void> => {
		const { wallet } = this.store.onboard;
		if (!wallet?.provider) {
			return;
		}
		const web3 = new Web3(wallet.provider);
		const wrappedIbbtc = new web3.eth.Contract(WIBBTC_ABI, WIBBTC);

		try {
			const ppfs = await wrappedIbbtc.methods.pricePerShare().call();
			this.setPricePerShare(ppfs);
		} catch (err) {
			console.error(err);
		}
	});

	private setPricePerShare = action((ppfs: string) => {
		this.pricePerShare = new BigNumber(ppfs);
	});

	updateBalances = action(async (addressOverride?: string, cached?: boolean): Promise<void> => {
		const { address, wallet } = this.store.onboard;

		/**
		 * only allow one set of calls at a time, blocked by a loading guard
		 * do not update balances without prices available or a provider
		 */
		const queryAddress = addressOverride ?? address;
		if (!queryAddress || this.loadingBalances || !wallet?.provider) {
			return;
		}

		this.loadingBalances = true;
		const cacheKey = `${address}`;

		if (cached) {
			const cachedBalances = this.userBalanceCache[cacheKey];
			if (cachedBalances && Date.now() <= cachedBalances.expiry) {
				this.setBalances(cachedBalances);
				this.loadingBalances = false;
				return;
			}
		}

		try {
			const web3 = new Web3(wallet.provider);
			const ibBTC = new web3.eth.Contract(IBBTC_ABI, IBBTC);
			const wibBTC = new web3.eth.Contract(WIBBTC_ABI, WIBBTC);

			const [ibBTCBalance, wibBTCBalance] = await Promise.all([
				ibBTC.methods.balanceOf(address).call(),
				wibBTC.methods.balanceOf(address).call()
			]);

			const tokenBalances: TokenBalances = {
				[IBBTC]: {
					balance: new BigNumber(ibBTCBalance),
					displayBalance: new BigNumber(ibBTCBalance).dividedBy(1e18).toFixed(6).toString()
				},
				[WIBBTC]: {
					balance: new BigNumber(wibBTCBalance),
					displayBalance: new BigNumber(wibBTCBalance).dividedBy(1e18).toFixed(6).toString()
				}
			};

			const result = {
				key: cacheKey,
				tokens: tokenBalances,
				expiry: Date.now() + 5 * ONE_MIN_MS
			};

			this.userBalanceCache[cacheKey] = result;
			this.setBalances(result);
			this.loadingBalances = false;
		} catch (err) {
			console.error(err);
			this.loadingBalances = false;
		}
	});

	private setBalances = action((balances: CachedTokenBalances): void => {
		const { tokens } = balances;
		this.tokenBalances = tokens;
	});

	private getOrDefaultBalance(balances: TokenBalances, token: string): TokenBalance {
		const balance = balances[token];
		if (!balance) {
			return {
				balance: new BigNumber(0),
				displayBalance: '0'
			};
		}
		return balance;
	}

	getTokenBalance(contract: string): TokenBalance {
		const tokenAddress = Web3.utils.toChecksumAddress(contract);
		return this.getOrDefaultBalance(this.tokenBalances, tokenAddress);
	}

	clearBalances() {
		this.setBalances({
			key: '',
			tokens: {},
			expiry: 0
		});
	}
}
